import axios from "axios";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { pdfjs } from "react-pdf";
import { createWorker, Worker as TesseractWorker } from "tesseract.js";
import {
  PdfRedactionArea,
  RedactionFootnote,
  RedactionType,
} from "../db/schema";

// All tesseract assets are served from our own origin (vendored into
// public/tesseract/) — no CDN egress, which matters for CJIS deployments.
const TESSERACT_ASSETS = {
  workerPath: "/tesseract/worker.min.js",
  corePath: "/tesseract",
  langPath: "/tesseract",
};

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

// Swaps a private redaction-document S3 URL for a short-lived presigned GET
// URL. Non-S3 URLs (blob:, data:) are returned unchanged.
export async function resolveRedactionPdfUrl(
  originalUrl: string | null | undefined
): Promise<string> {
  if (!originalUrl) return "";
  if (!originalUrl.includes("police-narratives-ai-form-templates")) {
    return originalUrl;
  }
  try {
    const res = await axios.post("/api/redaction-documents/view-url", {
      url: originalUrl,
    });
    return res.data.url;
  } catch (err) {
    console.error("Failed to resolve redaction PDF URL", err);
    return "";
  }
}

// --- Text extraction -------------------------------------------------------

// One pdf.js text item flattened into page space: unscaled PDF points with a
// top-left origin (the same coordinate system FormFieldDefinition.rect uses).
interface PageTextItem {
  str: string;
  x: number;
  y: number; // top edge
  width: number;
  height: number;
  charStart: number; // offset of this item's first char in the page string
  fontFamily?: string; // pdf.js style hint (serif/sans-serif/monospace)
}

export interface PageTextData {
  page: number; // 1-indexed
  text: string; // concatenated page text (items joined with single spaces)
  items: PageTextItem[];
  pageWidth: number;
  pageHeight: number;
  ocr: boolean; // true when the text came from OCR, not the PDF text layer
}

export type ExtractProgress = {
  phase: "extract" | "ocr";
  page: number;
  total: number;
};

const OCR_RENDER_SCALE = 2;

// OCR a page that has no embedded text layer (scanned document). The page is
// rasterized to a canvas and run through tesseract; word bounding boxes come
// back in raster pixels and are divided back down to PDF points.
async function ocrPageItems(
  worker: TesseractWorker,
  page: any
): Promise<{ items: PageTextItem[]; text: string }> {
  const viewport = page.getViewport({ scale: OCR_RENDER_SCALE });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create canvas context for OCR");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport } as any).promise;

  const { data } = await worker.recognize(canvas, {}, { blocks: true });

  const items: PageTextItem[] = [];
  let pageText = "";
  for (const block of data.blocks ?? []) {
    for (const paragraph of block.paragraphs ?? []) {
      for (const line of paragraph.lines ?? []) {
        for (const word of line.words ?? []) {
          const str = word.text?.trim();
          if (!str) continue;
          if (pageText.length > 0) pageText += " ";
          const charStart = pageText.length;
          pageText += str;
          items.push({
            str,
            x: word.bbox.x0 / OCR_RENDER_SCALE,
            y: word.bbox.y0 / OCR_RENDER_SCALE,
            width: (word.bbox.x1 - word.bbox.x0) / OCR_RENDER_SCALE,
            height: (word.bbox.y1 - word.bbox.y0) / OCR_RENDER_SCALE,
            charStart,
          });
        }
      }
    }
  }
  return { items, text: pageText };
}

// Extract the text layer of every page, keeping a char-offset → item mapping
// so verbatim matches can be resolved back to page rects. Pages without an
// embedded text layer (scans) fall back to OCR, so downstream code — AI
// identification and text-selection redaction — works the same either way.
export async function extractPdfText(
  pdfUrl: string,
  onProgress?: (progress: ExtractProgress) => void
): Promise<PageTextData[]> {
  const doc = await pdfjs.getDocument(pdfUrl).promise;
  const pages: PageTextData[] = [];
  let ocrWorker: TesseractWorker | null = null;

  try {
    for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
      onProgress?.({ phase: "extract", page: pageNum, total: doc.numPages });
      const page = await doc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const textContent = await page.getTextContent();

      const items: PageTextItem[] = [];
      let pageText = "";
      let prev: PageTextItem | null = null;

      for (const raw of textContent.items) {
        const item = raw as any;
        if (typeof item.str !== "string" || item.str.length === 0) continue;

        // transform = [a, b, c, d, e, f] in bottom-left PDF coords; for
        // horizontal text the glyph height is the scale of the c/d column and
        // (e, f) is the baseline origin.
        const fontHeight =
          Math.hypot(item.transform[2], item.transform[3]) || 10;
        const x = item.transform[4];
        const baselineY = item.transform[5];
        // Box from ~ascent above the baseline to slightly below it so
        // descenders are covered.
        const y = viewport.height - baselineY - fontHeight;
        const height = fontHeight * 1.2;

        // pdf.js frequently splits a single word into several items (kerning,
        // font changes), so a space is only inserted when the items are on
        // different lines or visibly apart — otherwise "John" would become
        // "Jo hn" and verbatim matching would fail.
        if (prev && pageText.length > 0 && !pageText.endsWith(" ")) {
          const newLine = Math.abs(y - prev.y) > fontHeight * 0.5;
          const gap = x - (prev.x + prev.width);
          if (newLine || gap > fontHeight * 0.15) {
            pageText += " ";
          }
        }
        const charStart = pageText.length;
        pageText += item.str;

        const flattened: PageTextItem = {
          str: item.str,
          x,
          y,
          width: item.width,
          height,
          charStart,
          fontFamily: (textContent as any).styles?.[item.fontName]?.fontFamily,
        };
        items.push(flattened);
        prev = flattened;
      }

      // No embedded text on this page — it's a scan. OCR it so redaction
      // still works.
      if (pageText.trim().length === 0) {
        onProgress?.({ phase: "ocr", page: pageNum, total: doc.numPages });
        if (!ocrWorker) {
          ocrWorker = await createWorker("eng", 1, TESSERACT_ASSETS);
        }
        const ocrResult = await ocrPageItems(ocrWorker, page);
        pages.push({
          page: pageNum,
          text: ocrResult.text,
          items: ocrResult.items,
          pageWidth: viewport.width,
          pageHeight: viewport.height,
          ocr: true,
        });
        continue;
      }

      pages.push({
        page: pageNum,
        text: pageText,
        items,
        pageWidth: viewport.width,
        pageHeight: viewport.height,
        ocr: false,
      });
    }
  } finally {
    doc.destroy();
    if (ocrWorker) await ocrWorker.terminate();
  }

  return pages;
}

// --- Matching --------------------------------------------------------------

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Shared canvas context for substring-width measurement.
let sharedMeasureCtx: CanvasRenderingContext2D | null = null;
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
  if (!sharedMeasureCtx) {
    sharedMeasureCtx = document.createElement("canvas").getContext("2d");
  }
  return sharedMeasureCtx;
};

// Fractions (0..1) of an item's rendered width where the [from, to) char
// range starts and ends, based on measured proportional glyph widths.
// Falls back to uniform character widths if canvas isn't available.
function substringFractions(
  str: string,
  from: number,
  to: number,
  fontFamily?: string
): [number, number] {
  const ctx = getMeasureCtx();
  if (ctx && str.length > 0) {
    ctx.font = `100px ${fontFamily || "sans-serif"}`;
    const total = ctx.measureText(str).width;
    if (total > 0) {
      return [
        ctx.measureText(str.slice(0, from)).width / total,
        ctx.measureText(str.slice(0, to)).width / total,
      ];
    }
  }
  const len = str.length || 1;
  return [from / len, to / len];
}

// Same tolerance as the editor's RedactionsExtension: case-insensitive by
// default, any whitespace run in the needle matches any whitespace run in
// the page.
function buildNeedleRegex(
  needle: string,
  caseSensitive = false
): RegExp | null {
  const trimmed = needle.trim();
  if (!trimmed) return null;
  const pattern = trimmed
    .split(/\s+/)
    .map(escapeRegExp)
    .join("\\s+");
  try {
    return new RegExp(pattern, caseSensitive ? "gu" : "giu");
  } catch {
    return null;
  }
}

// Find every occurrence of `needle` on a page and return covering rects.
// A match spanning multiple text items produces one rect per line run —
// horizontally adjacent segments with similar vertical position are merged.
export function locateTextMatches(
  pageData: PageTextData,
  needle: string,
  options: { caseSensitive?: boolean } = {}
): { rects: { x: number; y: number; width: number; height: number }[] }[] {
  const regex = buildNeedleRegex(needle, options.caseSensitive);
  if (!regex) return [];

  const matches: {
    rects: { x: number; y: number; width: number; height: number }[];
  }[] = [];

  let m: RegExpExecArray | null;
  while ((m = regex.exec(pageData.text)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    const segments: { x: number; y: number; width: number; height: number }[] =
      [];

    for (const item of pageData.items) {
      const itemStart = item.charStart;
      const itemEnd = item.charStart + item.str.length;
      const overlapStart = Math.max(start, itemStart);
      const overlapEnd = Math.min(end, itemEnd);
      if (overlapStart >= overlapEnd) continue;

      // Sub-item offsets from measured substring widths. We can't measure
      // with the PDF's embedded font, but character-width *proportions* are
      // close across fonts of the same family, so the fractions land far
      // more accurately than assuming uniform character widths.
      const [startFrac, endFrac] = substringFractions(
        item.str,
        overlapStart - itemStart,
        overlapEnd - itemStart,
        item.fontFamily
      );
      const segX = item.x + item.width * startFrac;
      const segWidth = item.width * (endFrac - startFrac);

      segments.push({
        x: segX,
        y: item.y,
        width: segWidth,
        height: item.height,
      });
    }

    if (segments.length === 0) continue;

    matches.push({ rects: mergeLineRects(segments) });
  }

  return matches;
}

type Rect = { x: number; y: number; width: number; height: number };

// Merge rects on the same visual line into single runs.
function mergeLineRects(segments: Rect[]): Rect[] {
  const sorted = [...segments].sort((a, b) => a.y - b.y || a.x - b.x);
  const merged: Rect[] = [];
  for (const seg of sorted) {
    const last = merged[merged.length - 1];
    if (
      last &&
      Math.abs(last.y - seg.y) < seg.height * 0.6 &&
      seg.x <= last.x + last.width + seg.height * 1.5
    ) {
      const right = Math.max(last.x + last.width, seg.x + seg.width);
      last.x = Math.min(last.x, seg.x);
      last.width = right - last.x;
      last.y = Math.min(last.y, seg.y);
      last.height = Math.max(last.height, seg.height);
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

// Clean up rects produced by window.getSelection().getClientRects(): the
// browser reports a rect per text node plus wrapper rects for fully-selected
// elements. Drop rects that contain other rects, then merge per-line runs so
// a selection yields one bar per visual line.
export function normalizeSelectionRects(rects: Rect[]): Rect[] {
  const meaningful = rects.filter((r) => r.width > 0.5 && r.height > 0.5);
  const contains = (a: Rect, b: Rect) =>
    b.x >= a.x - 0.5 &&
    b.y >= a.y - 0.5 &&
    b.x + b.width <= a.x + a.width + 0.5 &&
    b.y + b.height <= a.y + a.height + 0.5 &&
    (a.width > b.width + 1 || a.height > b.height + 1);
  const withoutWrappers = meaningful.filter(
    (a) => !meaningful.some((b) => b !== a && contains(a, b))
  );
  return mergeLineRects(withoutWrappers);
}

// --- AI identification -----------------------------------------------------

const genId = () =>
  `redaction-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export interface IdentifyResult {
  areas: PdfRedactionArea[];
  footnotes: RedactionFootnote[];
  identifiedCount: number;
  unmatched: number;
}

// Thrown when the PDF yields no text at all (even after OCR) — the caller
// surfaces a "no text found" message.
export class NoTextError extends Error {
  constructor() {
    super("No text could be found in this PDF, even with OCR.");
    this.name = "NoTextError";
  }
}

// Run the Bedrock `identify-redactions` action against a document and resolve
// each returned verbatim string to page rects. Extracted so both the editor
// (interactive re-identify) and the list view (background identify after
// upload) share one implementation. Manual areas passed in are preserved;
// AI areas are rebuilt. Identified statutes/justifications fold into the
// managed footnote list (reusing entries with identical text).
export async function identifyDocumentRedactions(
  pdfUrl: string,
  opts: {
    docId: string;
    existingAreas?: PdfRedactionArea[];
    existingFootnotes?: RedactionFootnote[];
    reportPreferences?: unknown;
    // Officer-supplied scope for the identify pass. Sent explicitly so the
    // editor can revise it before re-running; the server persists it.
    redactionInstructions?: string;
    onProgress?: (progress: ExtractProgress) => void;
  }
): Promise<IdentifyResult> {
  const pages = await extractPdfText(pdfUrl, opts.onProgress);
  const fullText = pages
    .map((p) => p.text)
    .join("\n\n")
    .trim();
  if (!fullText) throw new NoTextError();

  const { data } = await axios.post(
    `/api/redaction-documents/${opts.docId}/identify`,
    {
      text: fullText,
      reportPreferences: opts.reportPreferences,
      redactionInstructions: opts.redactionInstructions,
    }
  );
  const identified: RedactionType[] = data.redactions || [];

  const nextFootnotes = [...(opts.existingFootnotes || [])];
  const footnoteByKey = new Map<string, RedactionFootnote>(
    nextFootnotes.map((f) => [
      `${f.statute?.trim() || ""}|${f.justification?.trim() || ""}`,
      f,
    ])
  );
  const footnoteFor = (item: RedactionType): string | undefined => {
    const statute = item.statute?.trim();
    const justification = item.justification?.trim();
    if (!statute && !justification) return undefined;
    const key = `${statute || ""}|${justification || ""}`;
    let fn = footnoteByKey.get(key);
    if (!fn) {
      fn = { id: genId(), statute, justification };
      footnoteByKey.set(key, fn);
      nextFootnotes.push(fn);
    }
    return fn.id;
  };

  const newAreas: PdfRedactionArea[] = [];
  let unmatched = 0;
  for (const item of identified) {
    let matchedAny = false;
    const footnoteId = footnoteFor(item);
    for (const pageData of pages) {
      for (const match of locateTextMatches(pageData, item.text)) {
        matchedAny = true;
        for (const rect of match.rects) {
          newAreas.push({
            id: genId(),
            groupId: item.id,
            page: pageData.page,
            rect,
            text: item.text,
            category: item.category,
            reason: item.reason,
            tag: item.tag,
            statute: item.statute,
            justification: item.justification,
            footnoteId,
            source: "ai",
            enabled: true,
          });
        }
      }
    }
    if (!matchedAny) unmatched++;
  }

  const manualAreas = (opts.existingAreas || []).filter(
    (a) => a.source === "manual"
  );
  return {
    areas: [...manualAreas, ...newAreas],
    footnotes: nextFootnotes,
    identifiedCount: identified.length,
    unmatched,
  };
}

// --- Export ----------------------------------------------------------------

interface PdfRedactionFootnotes {
  numberOf: Map<string, number>; // area id -> footnote number
  list: { n: number; statute?: string; justification?: string }[];
}

// Numbering from the document's user-managed footnote list: a footnote's
// number is its 1-based position in the list, and areas reference footnotes
// via footnoteId. Footnotes with no content are skipped.
function buildFootnotes(
  active: PdfRedactionArea[],
  footnotes: RedactionFootnote[]
): PdfRedactionFootnotes {
  const numberOf = new Map<string, number>();
  const list: PdfRedactionFootnotes["list"] = [];
  const numberOfFootnote = new Map<string, number>();

  for (const f of footnotes) {
    if (!f.statute?.trim() && !f.justification?.trim()) continue;
    const n = list.length + 1;
    numberOfFootnote.set(f.id, n);
    list.push({
      n,
      statute: f.statute?.trim() || undefined,
      justification: f.justification?.trim() || undefined,
    });
  }

  for (const r of active) {
    if (!r.footnoteId) continue;
    const n = numberOfFootnote.get(r.footnoteId);
    if (n !== undefined) numberOf.set(r.id, n);
  }
  return { numberOf, list };
}

// Same line format as the editor export: "(statute) justification".
const footnoteLine = (f: { statute?: string; justification?: string }) =>
  `${f.statute ? `(${f.statute}) ` : ""}${f.justification || ""}`.trim();

const EXPORT_RENDER_SCALE = 2;

export const DEFAULT_TAG_FONT_PT = 10;

export interface TagSizeOptions {
  /** true = one global size for every label; false = auto from bar height
   *  with optional per-redaction overrides. */
  uniformTagSize: boolean;
  /** Global label size (PDF points) used when uniformTagSize is true. */
  globalTagSize: number;
}

// Effective label size for a redaction, in PDF points. Shared by the editor
// overlay and the export so they always agree.
export function resolveTagFontPt(
  area: PdfRedactionArea,
  opts: TagSizeOptions
): number {
  if (opts.uniformTagSize) return opts.globalTagSize;
  return (
    area.tagFontSize ??
    Math.min(area.rect.height * 0.55, DEFAULT_TAG_FONT_PT)
  );
}

// Horizontal/vertical padding (PDF points) added around each bar so glyph
// edges never peek out. Exported so the editor overlay can mirror the
// export's label-visibility threshold exactly.
export const BAR_PADDING = 1.5;

// Generate the redacted PDF entirely client-side. Each page is rasterized to
// a canvas, black bars are painted over the redaction areas (with optional
// white tag labels, like the narrative export), and the flattened images are
// assembled into a new PDF. Because pages are flattened to images, the
// underlying text is unrecoverable — a true redaction, not an overlay.
export async function exportRedactedPdf(
  pdfUrl: string,
  redactions: PdfRedactionArea[],
  options: {
    showTags?: boolean;
    showFootnotes?: boolean;
    uniformTagSize?: boolean;
    globalTagSize?: number;
    footnotes?: RedactionFootnote[];
  } = {}
): Promise<Blob> {
  const {
    showTags = true,
    showFootnotes = true,
    uniformTagSize = true,
    globalTagSize = DEFAULT_TAG_FONT_PT,
    footnotes: footnoteList = [],
  } = options;
  const doc = await pdfjs.getDocument(pdfUrl).promise;
  const outputPdf = await PDFDocument.create();

  const active = redactions.filter((r) => r.enabled);
  const footnotes = showFootnotes
    ? buildFootnotes(active, footnoteList)
    : { numberOf: new Map<string, number>(), list: [] };

  let basePageSize: [number, number] = [612, 792];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale: EXPORT_RENDER_SCALE });

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not create canvas context");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport } as any).promise;

    for (const redaction of active.filter((r) => r.page === pageNum)) {
      const s = EXPORT_RENDER_SCALE;
      const x = (redaction.rect.x - BAR_PADDING) * s;
      const y = (redaction.rect.y - BAR_PADDING) * s;
      const width = (redaction.rect.width + BAR_PADDING * 2) * s;
      const height = (redaction.rect.height + BAR_PADDING * 2) * s;

      ctx.fillStyle = "#000000";
      ctx.fillRect(x, y, width, height);

      // White tag label plus a raised footnote number, matching the
      // narrative export's tagged-bar rendering. Label size comes from the
      // shared resolver (uniform / auto / per-redaction override); labels
      // wider than the bar shrink to fit (4pt floor) rather than being
      // dropped, and drawing is clipped to the bar so nothing can paint
      // outside it.
      const label = showTags && redaction.tag ? `[${redaction.tag}]` : "";
      const n = footnotes.numberOf.get(redaction.id);
      let fontSize =
        resolveTagFontPt(redaction, { uniformTagSize, globalTagSize }) * s;

      const measure = () => {
        ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`;
        const labelWidth = label ? ctx.measureText(label).width : 0;
        ctx.font = `${fontSize * 0.65}px Helvetica, Arial, sans-serif`;
        const supWidth =
          n !== undefined ? ctx.measureText(String(n)).width : 0;
        const supGap = n !== undefined ? 1.5 * s : 0;
        return { labelWidth, supGap, total: labelWidth + supGap + supWidth };
      };

      let m = measure();
      const available = width - 4 * s;
      if (m.total > available && m.total > 0) {
        fontSize = Math.max(4 * s, fontSize * (available / m.total));
        m = measure();
      }

      // Labels taller than the bar shrink to fit (4pt floor) instead of
      // being dropped — same policy as width overflow, mirrored by the
      // editor overlay.
      const maxFontByHeight = height - 2 * s;
      if (m.total > 0 && fontSize > maxFontByHeight) {
        fontSize = Math.max(4 * s, maxFontByHeight);
        m = measure();
      }

      if (m.total > 0 && height >= fontSize + 2 * s) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.clip();
        ctx.fillStyle = "#ffffff";
        ctx.textBaseline = "middle";
        const startX = x + Math.max(2 * s, (width - m.total) / 2);
        if (label) {
          ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`;
          ctx.fillText(label, startX, y + height / 2);
        }
        if (n !== undefined) {
          ctx.font = `${fontSize * 0.65}px Helvetica, Arial, sans-serif`;
          ctx.fillText(
            String(n),
            startX + m.labelWidth + m.supGap,
            y + height / 2 - fontSize * 0.3
          );
        }
        ctx.restore();
      }
    }

    const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const jpegImage = await outputPdf.embedJpg(jpegDataUrl);

    // Page keeps the original PDF-point dimensions; the 2x raster is just
    // for print-quality output.
    const baseViewport = page.getViewport({ scale: 1 });
    if (pageNum === 1) {
      basePageSize = [baseViewport.width, baseViewport.height];
    }
    const outputPage = outputPdf.addPage([
      baseViewport.width,
      baseViewport.height,
    ]);
    outputPage.drawImage(jpegImage, {
      x: 0,
      y: 0,
      width: baseViewport.width,
      height: baseViewport.height,
    });
  }

  doc.destroy();

  // Footnotes appendix — same header and "(statute) justification" line
  // format as the narrative export's footnotes section. Drawn as vector text
  // (it contains no PII, only the legal grounds for withholding).
  if (footnotes.list.length > 0) {
    const font = await outputPdf.embedFont(StandardFonts.Helvetica);
    const italic = await outputPdf.embedFont(StandardFonts.HelveticaOblique);
    const [pageWidth, pageHeight] = basePageSize;
    const margin = 54;
    const bodySize = 10;
    const supSize = 6.5;
    const lineHeight = 14;
    const maxWidth = pageWidth - margin * 2;

    let page = outputPdf.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    page.drawText("Footnotes (legal justification for redaction)", {
      x: margin,
      y,
      size: 11,
      font: italic,
    });
    y -= lineHeight * 1.5;

    for (const f of footnotes.list) {
      const numStr = String(f.n);
      const numWidth = font.widthOfTextAtSize(numStr, supSize) + 3;

      // Word-wrap the footnote text within the page margins.
      const words = footnoteLine(f).split(/\s+/);
      const lines: string[] = [];
      let current = "";
      for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (
          font.widthOfTextAtSize(test, bodySize) > maxWidth - numWidth &&
          current
        ) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);

      for (let i = 0; i < lines.length; i++) {
        if (y < margin) {
          page = outputPdf.addPage([pageWidth, pageHeight]);
          y = pageHeight - margin;
        }
        if (i === 0) {
          page.drawText(numStr, { x: margin, y: y + 3, size: supSize, font });
        }
        page.drawText(lines[i], {
          x: margin + numWidth,
          y,
          size: bodySize,
          font,
        });
        y -= lineHeight;
      }
      y -= 4;
    }
  }

  const pdfBytes = await outputPdf.save();
  return new Blob([pdfBytes as unknown as ArrayBuffer], { type: "application/pdf" });
}
